import {defineStore} from 'pinia';
import {send} from "@/composables/apiClient";
import {useNavigation} from "@/composables/globals";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {isCircleOwner} from "@/stores/circleOwner.mjs";

const SSO_COOKIE_NAME = 'sso_token';
const STORAGE_KEY = 'auth';

// Bearer token held in module-level memory only — never written to localStorage.
// On page reload the sso_token httpOnly cookie re-authenticates the session.
let _sessionToken = null;

export function getSessionToken() {
    return _sessionToken;
}

/// Adopt a bearer obtained outside the normal login, i.e. the one-time billing
/// handoff from the iOS app.
///
/// In memory only, deliberately: the token is never persisted, which is why
/// loadFromStorage() scrubs any `token` key it finds. Writing it to
/// localStorage would put a live credential where XSS can read it.
export function setSessionToken(token) {
    _sessionToken = token ?? null;
}

function loadFromStorage() {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        // Scrub any token previously written by old code so XSS cannot read it.
        if (parsed && 'token' in parsed) {
            delete parsed.token;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
        return parsed;
    } catch {
        return null;
    }
}

function persist(authObject) {
    if (authObject === null) {
        localStorage.removeItem(STORAGE_KEY);
        return;
    }
    // Never persist the Bearer token — keep only non-sensitive session fields.
    const {token: _ignored, ...safe} = authObject;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
}

export const useAuthStore = defineStore('authStore', {
    // Parse-once state. Previously every getter re-parsed the raw
    // JSON string on every access (5 getters × every render). Now
    // state holds the parsed object and getters are plain reads.
    state: () => ({
        auth: loadFromStorage(),
    }),

    getters: {
        user: (state) => state.auth?.user ?? null,
        menus: (state) => state.auth?.menus ?? null,
        portals: (state) => state.auth?.portals ?? null,
        configs: (state) => state.auth?.configs ?? null,
        consents: (state) => state.auth?.consents ?? null,
        // Server flagged the password as admin-reset: the user is authenticated
        // but must change it before reaching the app (the router guard enforces).
        mustChangePassword: (state) => !!state.auth?.mustChangePassword,
        // The active circle (company) and whether the signed-in user owns it.
        // Owner-only actions (billing, delete account, managing members,
        // editing trackers) are gated on this when viewing a joined circle.
        currentCircle: (state) => state.auth?.user?.company ?? null,
        // One implementation, shared with the test. See circleOwner.mjs.
        isCircleOwner: (state) => isCircleOwner(state.auth?.user),
        // Can manage the active circle's membership: the owner, or an adult
        // member promoted to admin (server sets is_circle_admin in profile).
        isCircleAdmin: (state) => {
            const u = state.auth?.user;
            if (!u || !u.company) return false;
            return u.company.owner_id === u.id || !!u.is_circle_admin;
        },
        // Under 18 (from the API). Minors can't join after signup, share a join
        // code, invite, or leave a circle on their own.
        isMinor: (state) => !!state.auth?.user?.is_minor,
        // A minor who isn't yet a member of anyone else's circle. They can't use
        // their own circle, so the app shows a holding screen until a parent
        // adds them or they accept an invite (set by the profile endpoint).
        inCircleLimbo: (state) => !!state.auth?.user?.circle_limbo,
        // Belongs to NO circle. True for a limbo minor too, so read
        // inCircleLimbo first where the two need different treatment.
        //
        // circle_limbo is minor-only BY DESIGN - it means "a parent has not
        // added you yet" - so an adult with no circle never sets it. Gating on
        // it alone let that user into the full shell with no circle behind it:
        // an aged-out minor (the flag vanishes on the 18th birthday) or anyone
        // removed from their last circle.
        hasNoCircle: (state) => !!state.auth?.user?.circle_none,
        // This circle has nothing to buy: a comped customer (billing_exempt) or
        // an app-store review account (special_access). The server collapses
        // both causes into one derived boolean on `company_special_flags`, which
        // rides on BOTH the login response and the profile, so reading it costs
        // no request.
        //
        // Requires a real `true`. Every ambiguous case - an older server that
        // sends no block, a missing key, an unexpected shape - reads as FALSE,
        // meaning purchasing is available. Hiding a paying customer's only route
        // to pay behind a version mismatch is far worse than one redundant menu
        // item on a comped circle.
        noPurchaseAvailable: (state) =>
            state.auth?.user?.company_special_flags?.no_purchase_available === true,
        // The active circle is suspended (company disabled, e.g. unpaid charges).
        // The app intercepts with a full-screen suspended notice.
        circleSuspended: (state) => !!state.auth?.user?.circle_suspended,
    },

    actions: {
        login(authData) {
            _sessionToken = authData.token ?? null;
            this.auth = {
                user: authData.user,
                menus: authData.menus,
                portals: authData.portals,
                configs: authData.settings,
                consents: authData.consents,
                // Persisted (inside auth) so the forced-change trap survives a
                // page reload. Normal logins / SSO / register omit this field
                // -> !!undefined -> false, so they are never trapped.
                mustChangePassword: !!authData.force_password_change,
            };
            persist(this.auth);
            return Promise.resolve(authData.user);
        },

        // Release the forced-password-change trap once the user has successfully
        // changed their password (called from ForceChangePassword.vue on success).
        clearMustChange() {
            if (!this.auth) return;
            this.auth = {...this.auth, mustChangePassword: false};
            persist(this.auth);
        },

        isAuthenticated() {
            return !!this.auth && !!this.user && this.user?.email_verified_at !== null;
        },

        updateAuthUser(user) {
            if (!this.auth) return;
            this.auth = {...this.auth, user};
            persist(this.auth);
        },

        updateAuthCompany(company) {
            if (!this.auth?.user) return;
            this.auth = {
                ...this.auth,
                user: {...this.auth.user, company},
            };
            persist(this.auth);
        },

        updateAuthConfig(configs) {
            if (!this.auth) return;
            this.auth = {...this.auth, configs};
            persist(this.auth);
        },

        updateConsents(consents) {
            if (!this.auth) return;
            this.auth = {...this.auth, consents};
            persist(this.auth);
        },

        // Refresh the active circle and the user's full circle list from the
        // server. Login only returns the other circles (no owner_id), so we
        // pull the canonical set from the profile endpoint on app load and
        // merge it into the stored user, keeping login-only fields intact.
        async hydrateProfile() {
            if (!this.auth?.user) return;
            try {
                const res = await send('get', 'users/profile');
                const profile = res?.data?.data;
                if (profile) {
                    const merged = {...this.auth.user, ...profile};
                    // Normalize the gate flags so a stale `true` from a previous
                    // session can't survive a shallow merge if the server omits
                    // the key (e.g. a minor who just joined a circle would
                    // otherwise stay soft-locked in limbo).
                    merged.circle_limbo = profile.circle_limbo ?? false;
                    merged.is_minor = profile.is_minor ?? false;
                    merged.is_circle_admin = profile.is_circle_admin ?? false;
                    // Same rule, and it matters MORE for this one: the getter
                    // PREFERS the flag when it is a boolean, so a stale true
                    // surviving a shallow merge would grant billing access
                    // permanently, with the fallback unreachable. api #1329
                    // omits the key entirely for admin user types.
                    merged.is_circle_owner = profile.is_circle_owner ?? false;
                    // Normalize like the others: a healthy profile that omits the
                    // key must clear a stale true, else the suspended screen sticks.
                    merged.circle_suspended = profile.circle_suspended ?? false;
                    // Same normalization, same reason: a stale true surviving a
                    // shallow merge would soft-lock a user who has since joined
                    // a circle onto the holding screen.
                    merged.circle_none = profile.circle_none ?? false;
                    // Same rule again, and the direction matters: a stale true
                    // surviving a shallow merge would keep Plans hidden from a
                    // circle whose comp has ENDED - exactly the circle that now
                    // needs to buy something.
                    merged.company_special_flags = profile.company_special_flags ?? null;
                    this.auth = {...this.auth, user: merged};
                    persist(this.auth);
                }
            } catch (e) {
                // Non-fatal: the switcher just falls back to login-time data.
            }
        },

        updateAuth(data) {
            if (!this.auth) return;
            const next = {...this.auth};
            if (data.user) next.user = data.user;
            if (data.menus) next.menus = data.menus;
            if (data.portals) next.portals = data.portals;
            if (data.settings) next.configs = data.settings;
            if (data.consents) next.consents = data.consents;
            this.auth = next;
            persist(this.auth);
        },

        async logout() {
            try {
                if (this.auth) {
                    await send('post', `logout`);
                }
            } catch (error) {
                console.error('Logout failed:', error);
            } finally {
                this.unsetAuth();
                useSubscriptionStore().clear();
                useNavigation().navigateTo('login');
            }
        },
        unsetAuth() {
            _sessionToken = null;
            this.auth = null;
            persist(null);
            document.cookie = `${SSO_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        },
    },
});
